<template>
  <div class="ranking-card">
    <div class="section-title">{{ title }}</div>
    <div class="table-scroll nps-device-scroll">
      <table class="ranking-table data-table">
        <thead>
          <tr>
            <th>#</th>
            <th @click="changeSort('name')">{{ entityLabel }} <span class="sort-indicator" :class="{ active: sortBy === 'name' }">{{ sortIcon('name') }}</span></th>
            <th @click="changeSort('responses')">Respuestas <span class="sort-indicator" :class="{ active: sortBy === 'responses' }">{{ sortIcon('responses') }}</span></th>
            <th @click="changeSort('promoter_pct')">Promotores <span class="sort-indicator" :class="{ active: sortBy === 'promoter_pct' }">{{ sortIcon('promoter_pct') }}</span></th>
            <th @click="changeSort('neutral_pct')">Neutros <span class="sort-indicator" :class="{ active: sortBy === 'neutral_pct' }">{{ sortIcon('neutral_pct') }}</span></th>
            <th @click="changeSort('detractor_pct')">Detractores <span class="sort-indicator" :class="{ active: sortBy === 'detractor_pct' }">{{ sortIcon('detractor_pct') }}</span></th>
            <th @click="changeSort('nps')">NPS <span class="sort-indicator" :class="{ active: sortBy === 'nps' }">{{ sortIcon('nps') }}</span></th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in sortedRows" :key="`${row.name}-${index}`">
            <td class="rank-cell">{{ index + 1 }}</td>
            <td>{{ row.name }}</td>
            <td>{{ row.responses }}</td>
            <td class="pct-positive">{{ formatPct(row.promoter_pct) }}</td>
            <td class="pct-neutral">{{ formatPct(row.neutral_pct) }}</td>
            <td class="pct-negative">{{ formatPct(row.detractor_pct) }}</td>
            <td class="nps-value">{{ row.nps }}</td>
            <td>
              <span class="badge" :class="statusClass(row.nps)">{{ statusLabel(row.nps) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NpsReplicaRankingTable',
  props: {
    title: {
      type: String,
      default: ''
    },
    entityLabel: {
      type: String,
      default: 'Name'
    },
    scrollHintLabel: {
      type: String,
      default: 'items'
    },
    rows: {
      type: Array,
      default: function () {
        return [];
      }
    },
    thresholds: {
      type: Object,
      default: function () {
        return {
          positive: 50,
          neutral: 0
        };
      }
    }
  },
  data () {
    return {
      sortBy: 'nps',
      sortDesc: true
    };
  },
  computed: {
    sortedRows () {
      const rows = this.rows.slice();
      const sortBy = this.sortBy;
      const sortDesc = this.sortDesc;

      rows.sort(function (left, right) {
        if (left[sortBy] === right[sortBy]) {
          return String(left.name).localeCompare(String(right.name));
        }

        if (sortDesc) {
          return left[sortBy] < right[sortBy] ? 1 : -1;
        }

        return left[sortBy] > right[sortBy] ? 1 : -1;
      });

      return rows;
    }
  },
  methods: {
    changeSort (column) {
      if (this.sortBy === column) {
        this.sortDesc = !this.sortDesc;
        return;
      }

      this.sortBy = column;
      this.sortDesc = column !== 'name';
    },
    sortIcon (column) {
      if (this.sortBy !== column) {
        return '⇅';
      }

      return this.sortDesc ? '▼' : '▲';
    },
    formatPct (value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
    statusClass (nps) {
      if (nps >= this.thresholds.positive) {
        return 'status-positive';
      }

      if (nps >= this.thresholds.neutral) {
        return 'status-neutral';
      }

      return 'status-negative';
    },
    statusLabel (nps) {
      if (nps >= this.thresholds.positive) {
        return 'POSITIVE';
      }

      if (nps >= this.thresholds.neutral) {
        return 'NEUTRAL';
      }

      return 'NEGATIVE';
    }
  }
};
</script>

<style scoped>
.ranking-card {
  background: var(--bg-card, #FFFFFF);
  border-radius: 12px;
  box-shadow: var(--card-shadow, 0 2px 12px rgba(26,26,46,0.08));
  padding: 20px 22px;
}

.section-title {
  color: var(--text-primary, #1A1A2E);
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 16px;
}

.table-scroll {
  max-height: 380px;
  overflow: auto;
}

.nps-device-scroll {
  border-radius: 0 0 12px 12px;
  scrollbar-color: #E94560 #F0F2F8;
  scrollbar-width: thin;
}

.nps-device-scroll::-webkit-scrollbar {
  width: 6px;
}

.nps-device-scroll::-webkit-scrollbar-track {
  background: #F0F2F8;
}

.nps-device-scroll::-webkit-scrollbar-thumb {
  background: #E94560;
  border-radius: 3px;
}

.ranking-table {
  border-collapse: collapse;
  width: 100%;
}

.ranking-table th,
.ranking-table td {
  border-bottom: 1px solid #E2E8F0;
  font-size: 13px;
  padding: 7px 12px;
  text-align: left;
}

.ranking-table th {
  background: #1a1a2e;
  color: #fff;
  cursor: pointer;
  font-size: 11px;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  text-transform: uppercase;
  z-index: 2;
}

.ranking-table th:hover {
  background: #2a2a4a;
}

.ranking-table tbody tr:hover td {
  background: #F8FAFF;
}

.ranking-table tbody tr:last-child td {
  border-bottom: none;
}

.ranking-table th:first-child,
.ranking-table th:last-child {
  cursor: default;
}

.ranking-table th:first-child:hover,
.ranking-table th:last-child:hover {
  background: #1a1a2e;
}

.rank-cell {
  color: #6B7280;
  font-size: 11px;
  text-align: center;
  width: 24px;
}

.pct-positive {
  color: #28A745;
}

.pct-neutral {
  color: #FFC107;
}

.pct-negative {
  color: #DC3545;
}

.nps-value {
  font-weight: 700;
}

.sort-indicator {
  display: inline-block;
  min-width: 10px;
  opacity: 0.35;
}

.sort-indicator.active {
  opacity: 1;
}

.badge {
  border-radius: 999px;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  text-transform: uppercase;
}

.status-positive {
  background: #D4EDDA;
  color: #155724;
}

.status-neutral {
  background: #FFF3CD;
  color: #856404;
}

.status-negative {
  background: #F8D7DA;
  color: #721C24;
}
</style>



// WEBPACK FOOTER //
// src/components/client/rounds/nps-dashboard/components/RankingTable.vue